import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import MarketingLayout from './components/MarketingLayout'

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
}
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
}

const values = [
  {
    icon: '⚡',
    title: 'Speed',
    description: 'Every second counts in a home emergency. Our platform is engineered for real-time dispatch so homeowners can connect with qualified professionals quickly when urgent issues happen.',
  },
  {
    icon: '🔒',
    title: 'Trust',
    description: 'We verify every provider\'s license and insurance before they can accept a single job. Your home and family are protected.',
  },
  {
    icon: '📱',
    title: 'Simplicity',
    description: 'No apps. No data tracking. Just send a text - our system does the rest, guiding you step by step.',
  },
  {
    icon: '💚',
    title: 'Community',
    description: 'We support local, licensed service professionals and help them build sustainable businesses through consistent, quality leads.',
  },
  {
    icon: '💳',
    title: 'Transparency',
    description: 'Flat rate membership fee and trip charge. Direct billed work by contractor will vary depending on the nature and scale of the issue',
  },
  {
    icon: '⭐',
    title: 'Quality',
    description: 'Every completed job is rated by the customer. We continuously monitor provider performance to ensure excellence.',
  },
]

const customerBenefits = [
  { text: 'Immediate access to licensed plumbers, electricians, and HVAC technicians' },
  { text: 'No app download needed - dispatch via SMS from any phone' },
  { text: 'Transparent, flat rate membership fee and trip charge' },
  { text: 'License- and insurance-verified providers only' },
  { text: 'Real-time updates from dispatch to job completion' },
  { text: 'Rate your provider after each service to ensure quality' },
]

const providerBenefits = [
  { text: 'Receive qualified job leads in your service area automatically' },
  { text: 'Set your own availability schedule based on your service area and capacity' },
  { text: 'Streamlined Stripe Connect payouts after every completed job' },
  { text: 'Manage your team of technicians under your provider account' },
  { text: 'Build your reputation through customer ratings and reviews' },
  { text: 'Full admin dashboard to track jobs, payments, and performance' },
]

function CheckIcon() {
  return (
    <svg className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

function AboutPage() {
  useEffect(() => {
    document.title = 'About SOS SMS'
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      'Learn why SOS SMS was built, how it connects homeowners with licensed providers, and the values behind the platform.'
    )
  }, [])

  return (
    <MarketingLayout>

      {/* ── HERO ──────────────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-green-900 via-green-800 to-emerald-700 text-white py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid2" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid2)" />
          </svg>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.p variants={fadeUp} className="text-green-300 font-semibold text-sm uppercase tracking-wider mb-4">
              About Us
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
              Built for the Moments
              <br />
              <span className="text-green-300">That Can't Wait</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-green-100 text-lg leading-relaxed max-w-2xl mx-auto">
              SOS SMS was created with a single obsession: make emergency home services as fast and effortless as sending a text message.
            </motion.p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" className="w-full h-12 fill-white" preserveAspectRatio="none">
            <path d="M0,32 C360,60 1080,0 1440,32 L1440,60 L0,60 Z" />
          </svg>
        </div>
      </section>

      {/* ── COMPANY OVERVIEW ──────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
                Our Story
              </motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
                What Is SOS SMS?
              </motion.h2>
              <motion.div variants={fadeUp} className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  SOS SMS is an SMS-driven emergency home services dispatch platform that connects homeowners with licensed, insured, and immediately available service professionals in their area - within minutes.
                </p>
                <p>
                  We built SOS SMS because we experienced firsthand how broken the process of finding urgent home help had become. Searching online directories, waiting on hold, getting ghosted by contractors - all while your basement floods or your power flickers.
                </p>
                <p>
                  We asked a simple question: what if dispatching a plumber was as easy as sending a text? SOS SMS is the answer. Our intelligent dispatch engine matches the urgency of real-world emergencies with the speed of modern technology.
                </p>
              </motion.div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── MISSION & VISION ──────────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="bg-white rounded-3xl p-10 border border-gray-100 shadow-sm"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <motion.h3 variants={fadeUp} className="text-2xl font-extrabold text-gray-900 mb-4">Our Mission</motion.h3>
              <motion.p variants={fadeUp} className="text-gray-600 leading-relaxed">
                To eliminate the friction between homeowners in need and qualified service professionals - making emergency home services available to everyone, instantly, through a simple text message.
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="bg-gradient-to-br from-green-800 to-emerald-600 rounded-3xl p-10 text-white shadow-sm"
            >
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <motion.h3 variants={fadeUp} className="text-2xl font-extrabold mb-4">Our Vision</motion.h3>
              <motion.p variants={fadeUp} className="text-green-100 leading-relaxed">
                A world where every homeowner, regardless of location or time of day, can access reliable, licensed home service professionals within minutes - and every skilled tradesperson has consistent access to qualified customers.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── CORE VALUES ───────────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              What We Stand For
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Our Core Values
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {values.map((v) => (
              <motion.div
                key={v.title}
                variants={fadeUp}
                className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-primary/30 hover:bg-green-50/30 transition-colors"
              >
                <div className="text-3xl mb-4">{v.icon}</div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── BENEFITS ──────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              Benefits
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Built for Everyone
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto">
              SOS SMS delivers value to both sides of the marketplace - homeowners get fast help, providers get reliable work.
            </motion.p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-10">
            {/* Customer Benefits */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="bg-white rounded-3xl p-10 border border-gray-100 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <h3 className="text-xl font-extrabold text-gray-900">For Homeowners</h3>
              </div>
              <ul className="space-y-4">
                {customerBenefits.map((b, i) => (
                  <motion.li key={i} variants={fadeUp} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-600 text-sm leading-relaxed">{b.text}</span>
                  </motion.li>
                ))}
              </ul>
              <div className="mt-8">
                <Link
                  to="/customer/signup"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-green-700 transition-colors text-sm"
                >
                  Sign Up as Customer
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </motion.div>

            {/* Provider Benefits */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="bg-white rounded-3xl p-10 border border-gray-100 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-extrabold text-gray-900">For Service Providers</h3>
              </div>
              <ul className="space-y-4">
                {providerBenefits.map((b, i) => (
                  <motion.li key={i} variants={fadeUp} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-600 text-sm leading-relaxed">{b.text}</span>
                  </motion.li>
                ))}
              </ul>
              <div className="mt-8">
                <Link
                  to="/provider/signup"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-green-700 transition-colors text-sm"
                >
                  Join as Provider
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-5">
              Ready to Experience SOS SMS?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg mb-8">
              Whether you need emergency help at home or want to grow your service business, SOS SMS is the platform for you.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/customer/signup"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-green-700 transition-colors shadow-md"
              >
                Get Help Now
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-primary hover:text-primary transition-colors"
              >
                Contact Us
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </MarketingLayout>
  )
}

export default AboutPage
